import { Box, Card, Flex, Heading, List, ListIcon, ListItem } from '@chakra-ui/react';

import { ButtonLink } from '~/components/button-link';
import { Markdown } from '~/components/markdown';
import { trackEvent } from '~/features/analytics/track-event';
import { getCatalogItemsFromServer } from '~/features/catalog/controllers/get-catalog-items-from-server';
import { ProgramContent } from '~/features/program/models/program-content';

import { ArrowRightIcon, CheckIcon } from '../icons';

import { CatalogCard } from './catalog-card';

export type UpsellProgramAsideProps = NonNullable<StaticProgramUpsellAsideProps>;

export function UpsellProgramAside({ title, description, catalogItem }: UpsellProgramAsideProps) {
  return (
    <Card
      alignItems={{ md: 'center' }}
      as='aside'
      bgGradient='linear(180deg, #0E3FB2 0%, #368DFF 100%)'
      display='flex'
      flexDirection={{
        base: 'column-reverse',
        md: 'row',
      }}
      gap={{
        base: 4,
        md: '40px',
      }}
      padding={{
        base: '24px',
      }}
    >
      <Flex
        color='white'
        flexDirection='column'
        gap={6}
        flex={{
          md: '0 1 543px',
        }}
      >
        <Flex flexDirection='column' gap={4}>
          <Heading
            as='h2'
            size={{
              base: 'heading-6',
              md: 'heading-5',
            }}
          >
            {title}
          </Heading>
          <Markdown
            markdownText={description}
            components={{
              ul: ({ children }) => (
                <List display='flex' flexDir='column' gap={2}>
                  {children}
                </List>
              ),
              li: ({ children }) => (
                <ListItem
                  display='flex'
                  fontSize={{
                    base: 'sm',
                    md: 'md',
                  }}
                  lineHeight={{
                    base: 'sm',
                    md: 'md',
                  }}
                >
                  <ListIcon as={CheckIcon} color='supportBlue.40' height='24px' verticalAlign='center' width='24px' />
                  {children}
                </ListItem>
              ),
            }}
          />
        </Flex>
        <ButtonLink
          buttonProps={{
            variant: 'solid',
            width: {
              base: '100%',
              md: 'fit-content',
            },
            size: 'icon-right',
            colorScheme: 'white',
            rightIcon: <ArrowRightIcon height='20px' width='20px' />,
            onClick: () =>
              trackEvent({
                type: 'click',
                label: 'upsell_program_learn_more_clicked',
                name: 'Product Page CTA Clicked',
                value: catalogItem.title!,
                additionalAttributes: {
                  product_ids: [catalogItem.key].filter(Boolean),
                },
              }),
          }}
          linkProps={{
            href: `/course/${catalogItem.slug}`,
          }}
        >
          Learn More
        </ButtonLink>
      </Flex>
      <Box flexShrink={{ md: 0 }} maxWidth={{ md: '237px' }} width='100%'>
        <CatalogCard
          catalogItem={catalogItem}
          width='100%'
          priority
          onClick={() =>
            trackEvent({
              type: 'click',
              label: 'upsell_program_clicked',
              name: 'Product Page CTA Clicked',
              value: catalogItem.title!,
              additionalAttributes: {
                product_ids: [catalogItem.key].filter(Boolean),
              },
            })
          }
        />
      </Box>
    </Card>
  );
}

export function transformStaticProgramUpsellAsideProps({ programContent }: { programContent: ProgramContent }) {
  const key = programContent.upsellPrograms?.[0]?.key;
  const DEFAULT_TITLE = 'Subscribe today and unlock access to the entire Nanodegree program' as const;
  const DEFAULT_FEATURES = [
    '- **Cancel Anytime**',
    '- **Unlimited access to hundreds of top-rated courses**',
    '- Personalized Career Services',
    '- Real-world projects reviewed by expert mentors',
  ] as const;
  const DEFAULT_DESCRIPTION = `
  ${DEFAULT_FEATURES.join('\n')}
  `;
  // Note: we only show the upsell program if the program is free.
  if (!key || !programContent?.isFree) {
    return null;
  }

  const catalogItem = getCatalogItemsFromServer([key])?.[0];

  if (!catalogItem) {
    return null;
  }

  return {
    title: programContent.upsellProgramsSectionCopy?.title ?? DEFAULT_TITLE,
    description: programContent.upsellProgramsSectionCopy?.body ?? DEFAULT_DESCRIPTION,
    catalogItem,
  };
}

export type StaticProgramUpsellAsideProps = ReturnType<typeof transformStaticProgramUpsellAsideProps>;
